import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Story {
  id: string;
  user_id: string;
  title?: string | null;
  media_url: string;
  media_type: 'photo' | 'video';
  thumbnail_url?: string | null;
  duration: number;
  display_duration?: number;
  is_active: boolean;
  view_count: number;
  created_at: string;
  expires_at: string;
  link_url?: string | null;
  button_text?: string | null;
  alt_text?: string | null;
  profiles?: {
    first_name: string | null;
    last_name: string | null;
  } | null;
}

export const useStories = () => {
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchStories = async () => {
    try {
      console.log('Fetching stories...');
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .eq('is_active', true)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      console.log('Stories query result:', { data, error });

      if (error) {
        console.error('Stories fetch error:', error);
        throw error;
      }
      
      // Transform the data and get profiles separately
      const transformedStories = (data || []).map(story => ({
        ...story,
        media_type: story.media_type as 'photo' | 'video',
        profiles: null // Will be populated below
      })) as Story[];
      
      // Get unique user IDs
      const userIds = [...new Set(transformedStories.map(s => s.user_id))];
      
      // Fetch profiles for all users at once
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, first_name, last_name')
          .in('id', userIds);
        
        // Map profiles to stories
        transformedStories.forEach(story => {
          const profile = profiles?.find(p => p.id === story.user_id);
          story.profiles = profile ? {
            first_name: profile.first_name,
            last_name: profile.last_name
          } : null;
        });
      }
      
      setStories(transformedStories);
    } catch (error) {
      console.error('Error fetching stories:', error);
      toast({
        title: 'Սխալ',
        description: 'Պատմությունները բեռնելիս սխալ տեղի ունեցավ',
        variant: 'destructive'
      });
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const viewStory = async (storyId: string) => {
    try {
      await supabase.rpc('increment_story_view_count', {
        story_id_param: storyId
      });
      
      // Update local state
      setStories(prev => prev.map(story => 
        story.id === storyId 
          ? { ...story, view_count: story.view_count + 1 }
          : story
      ));
    } catch (error) {
      console.error('Error viewing story:', error);
    }
  };

  const createStory = async (
    file: File, 
    title: string, 
    duration: number = 86400,
    authorId?: string,
    linkUrl?: string,
    buttonText?: string,
    altText?: string,
    displayDuration: number = 5000
  ) => {
    try {
      console.log('Starting story creation process...');
      
      // Validate file size (100MB limit)
      const maxSize = 100 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('Ֆայլի չափը չպետք է գերազանցի 100 ՄԲ-ը');
      }

      // Validate file type
      if (!file.type.match(/^(image|video)\//)) {
        throw new Error('Ընտրեք միայն ֆոտո կամ վիդեո ֆայլ');
      }
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Օգտատերը նույնականացված չէ');

      // Use provided authorId or current user's id
      const effectiveUserId = authorId || user.id;

      console.log('User authenticated:', user.id);
      console.log('Story will be created for user:', effectiveUserId);
      console.log('File info:', { 
        name: file.name, 
        size: file.size, 
        type: file.type 
      });

      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (!fileExt) throw new Error('Ֆայլը չունի ընդլայնում');
      
      const fileName = `${effectiveUserId}/${Date.now()}.${fileExt}`;
      
      console.log('Uploading file:', fileName);
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('stories')
        .upload(fileName, file);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      console.log('File uploaded successfully:', uploadData);

      const { data: { publicUrl } } = supabase.storage
        .from('stories')
        .getPublicUrl(fileName);

      console.log('Public URL obtained:', publicUrl);

      const mediaType = file.type.startsWith('image/') ? 'photo' : 'video';
      
      const storyData = {
        user_id: effectiveUserId,
        title,
        media_url: publicUrl,
        media_type: mediaType,
        duration,
        display_duration: displayDuration,
        expires_at: new Date(Date.now() + duration * 1000).toISOString(),
        link_url: linkUrl || null,
        button_text: buttonText || null,
        alt_text: altText || null
      };
      
      console.log('Inserting story data:', storyData);
      
      const { data: insertData, error } = await supabase
        .from('stories')
        .insert(storyData);

      if (error) {
        console.error('Database insert error:', error);
        throw error;
      }

      console.log('Story inserted successfully:', insertData);

      toast({
        title: 'Հաջողություն',
        description: 'Պատմությունը հաջողությամբ ավելացվել է'
      });

      // Refresh stories immediately
      await fetchStories();
    } catch (error) {
      console.error('Error creating story:', error);
      toast({
        title: 'Սխալ',
        description: `Պատմության ավելացումը ձախողվեց: ${error.message || error}`,
        variant: 'destructive'
      });
      
      // Re-throw the error so the loading state is properly reset
      throw error;
    }
  };

  useEffect(() => {
    console.log('useStories useEffect triggered');
    fetchStories();

    // Subscribe to real-time updates
    const channel = supabase
      .channel('stories-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'stories' },
        (payload) => {
          console.log('Stories real-time update:', payload);
          fetchStories();
        }
      )
      .subscribe();

    return () => {
      console.log('Cleaning up stories subscription');
      supabase.removeChannel(channel);
    };
  }, []); // Empty dependency array to prevent infinite loops

  const getAllStories = async () => {
    try {
      console.log('Getting all stories for admin...');
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('All stories query result:', { data, error });

      if (error) {
        console.error('All stories fetch error:', error);
        throw error;
      }
      
      // Transform the data and get profiles separately
      const transformedStories = (data || []).map(story => ({
        ...story,
        media_type: story.media_type as 'photo' | 'video',
        profiles: null // Will be populated below
      })) as Story[];
      
      // Get unique user IDs
      const userIds = [...new Set(transformedStories.map(s => s.user_id))];
      
      // Fetch profiles for all users at once
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, first_name, last_name')
          .in('id', userIds);
        
        // Map profiles to stories
        transformedStories.forEach(story => {
          const profile = profiles?.find(p => p.id === story.user_id);
          story.profiles = profile ? {
            first_name: profile.first_name,
            last_name: profile.last_name
          } : null;
        });
      }
      
      return transformedStories;
    } catch (error) {
      console.error('Error fetching all stories:', error);
      return [];
    }
  };

  const updateStory = async (storyId: string, updates: Partial<Story>) => {
    try {
      console.log('Updating story with data:', { storyId, updates });
      
      const { error } = await supabase
        .from('stories')
        .update(updates)
        .eq('id', storyId);

      if (error) throw error;
      
      console.log('Story updated successfully');
      
      toast({
        title: 'Հաջողություն',
        description: 'Պատմությունը թարմացվել է'
      });
      
      // Wait for stories to refresh properly
      await fetchStories();
    } catch (error) {
      console.error('Error updating story:', error);
      toast({
        title: 'Սխալ',
        description: 'Պատմության թարմացումը ձախողվեց',
        variant: 'destructive'
      });
      throw error;
    }
  };

  const deleteStory = async (storyId: string) => {
    try {
      console.log('Attempting to delete story with ID:', storyId);
      
      // First, get the story to retrieve media_url
      const { data: story, error: fetchError } = await supabase
        .from('stories')
        .select('media_url, user_id')
        .eq('id', storyId)
        .single();

      if (fetchError) {
        console.error('Error fetching story for deletion:', fetchError);
        throw fetchError;
      }

      console.log('Story to delete:', story);

      // Delete the record from database first
      const { error: deleteError } = await supabase
        .from('stories')
        .delete()
        .eq('id', storyId);

      if (deleteError) {
        console.error('Database deletion error:', deleteError);
        throw deleteError;
      }

      console.log('Story deleted from database successfully');

      // Delete the media file from storage if it exists
      if (story?.media_url) {
        try {
          // Extract the file path from the URL
          // URL format: https://ivluchyoxbfcuwzdqdaj.supabase.co/storage/v1/object/public/stories/user_id/filename
          const urlParts = story.media_url.split('/');
          const storiesIndex = urlParts.findIndex(part => part === 'stories');
          if (storiesIndex !== -1 && storiesIndex < urlParts.length - 2) {
            const filePath = urlParts.slice(storiesIndex + 1).join('/');
            console.log('Deleting file from storage:', filePath);
            
            const { error: storageError } = await supabase.storage
              .from('stories')
              .remove([filePath]);
            
            if (storageError) {
              console.error('Storage deletion error:', storageError);
              // Don't throw here, story is already deleted from DB
            } else {
              console.log('File deleted from storage successfully');
            }
          }
        } catch (storageError) {
          console.error('Error deleting media file:', storageError);
          // Don't throw here, story is already deleted from DB
        }
      }
      
      toast({
        title: 'Հաջողություն',
        description: 'Պատմությունն ամբողջությամբ ջնջվել է'
      });
      
      // Force refresh
      await fetchStories();
    } catch (error) {
      console.error('Error deleting story:', error);
      toast({
        title: 'Սխալ',
        description: 'Պատմության ջնջումը ձախողվեց: ' + error.message,
        variant: 'destructive'
      });
      throw error;
    }
  };

  const toggleStoryActive = async (storyId: string, currentState: boolean) => {
    try {
      console.log('Toggling story:', storyId, 'from', currentState, 'to', !currentState);
      
      const { error } = await supabase
        .from('stories')
        .update({ is_active: !currentState })
        .eq('id', storyId);

      if (error) {
        console.error('Toggle error:', error);
        throw error;
      }
      
      toast({
        title: 'Հաջողություն',
        description: `Պատմությունը ${currentState ? 'անջատվեց' : 'միացվեց'}`
      });
      
      // Force refresh
      await fetchStories();
    } catch (error) {
      console.error('Error toggling story:', error);
      toast({
        title: 'Սխալ',
        description: 'Կարգավիճակը փոխելու ժամանակ սխալ է տեղի ունեցել: ' + error.message,
        variant: 'destructive'
      });
      throw error;
    }
  };

  return {
    stories,
    loading,
    viewStory,
    createStory,
    refetch: fetchStories,
    getAllStories,
    updateStory,
    deleteStory,
    toggleStoryActive
  };
};